import type { FC } from "react";
import {
    Banner,
    BannerType,
    ButtonColour,
    ButtonStyle,
    Link,
    LinkButton,
} from "@onshopfront/components";
import BoxForm from "../../Common/BoxForm/BoxForm";
import { useTitle } from "../../Utilities/TitleHook";
import { PartnerFitColumn } from "./PartnerFitColumn";
import { PartnerIconWithText } from "./PartnerIconWithText";
import { PartnerRegistrationBackground } from "./PartnerRegistrationBackground";
import { PartnerSection } from "./PartnerSection";
import { PartnerTimeline } from "./PartnerTimeline";

const SUBMISSION_CARD_CLASS = "bg-[var(--color-surface-muted)] rounded p-4 border border-[var(--color-border)]";

const PartnerProgram: FC = () => {
    useTitle("Partner Program");

    return (
        <main>
            <PartnerRegistrationBackground />
            <BoxForm className="w-6xl max-w-[90vw]" disableProse>
                <PartnerSection title="Partner Program" headingLevel={1}>
                    <p>
                        Our stores are looking for the best applications to connect to Shopfront.<br />
                        Join our partner program so they know how to find yours.
                    </p>
                </PartnerSection>
                <PartnerSection title="How it works" headingLevel={2}>
                    <PartnerTimeline />
                    <p className="text-xs text-[var(--color-text-tertiary)]">
                        Trial stores work the same as regular stores, and the trial period can be extended upon request.
                    </p>
                </PartnerSection>
                <PartnerSection title="Who it's for" headingLevel={2}>
                    <p>
                        The partner program is designed for software products that connect to Shopfront as a platform –
                        SaaS tools, platforms and services that integrate with multiple stores independently.
                    </p>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <PartnerFitColumn
                            positive
                            features={[
                                "SaaS products that connect to many stores (e.g. invoicing and ordering systems)",
                                "Platforms stores can discover and install without assistance (e.g. loyalty systems)",
                                "Software you sell or distribute to Shopfront customers (e.g. accounting systems)",
                            ]}
                        />
                        <PartnerFitColumn
                            positive={false}
                            features={[
                                "Custom integrations for a single store",
                                "Tailored integrations for specific clients",
                                "Companies that build bespoke solutions",
                            ]}
                        />
                    </div>
                    <Banner type={BannerType.Information}>
                        Building an integration for a single store? You don&apos;t need the partner program, instead
                        the store needs to be on Shopfront&apos;s premium plan and you need to put them through the
                        auth flow yourself.
                    </Banner>
                    <div className="mb-4" />
                </PartnerSection>
                <PartnerSection title="Benefits" headingLevel={2}>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <PartnerIconWithText icon="list">Listing in our integration marketplace</PartnerIconWithText>
                        <PartnerIconWithText icon="bullhorn">
                            Marketing opportunities directly to our customers
                        </PartnerIconWithText>
                        <PartnerIconWithText icon="exclamation">
                            Priority developer support
                        </PartnerIconWithText>
                        <PartnerIconWithText icon="plug">
                            Simplified setup for stores
                        </PartnerIconWithText>
                        <PartnerIconWithText icon="dollar-sign">
                            Stores can integrate without the premium plan
                        </PartnerIconWithText>
                    </div>
                </PartnerSection>
                <PartnerSection title="Requirements" headingLevel={2}>
                    <p>
                        The requirements are provided to ensure the highest quality of service for our stores and to
                        ensure their data security.
                    </p>
                    <div className="mb-4 space-y-4">
                        <div
                            className={
                                "rounded-xl bg-[var(--pill-red-bg)] text-[var(--pill-red-fg)] py-1 px-2 max-w-max"
                            }
                        >
                            Required for review
                        </div>
                        <PartnerIconWithText icon="check">OAuth 2.0 state parameter used</PartnerIconWithText>
                        <PartnerIconWithText icon="check">
                            <Link to="/documentation/General/Scopes" external>
                                Authorization scopes used
                            </Link>
                        </PartnerIconWithText>
                        <PartnerIconWithText icon="check">Support for TLS 1.3</PartnerIconWithText>
                        <PartnerIconWithText icon="check">Security self-assessment completed</PartnerIconWithText>
                        <PartnerIconWithText icon="check">Public privacy policy for user data</PartnerIconWithText>
                        <PartnerIconWithText icon="check">Emergency contact details provided</PartnerIconWithText>
                        <PartnerIconWithText icon="check">Public support contact / help centre</PartnerIconWithText>
                        {/* TODO: Define what these are SHOPFRONT-10314
                        <PartnerIconWithText icon="check">Shopfront brand guidelines followed</PartnerIconWithText>
                        */}
                        <PartnerIconWithText icon="check">
                            <Link to="/documentation/How-To/Direct-Authentication" external>
                                Direct authentication supported
                            </Link>
                        </PartnerIconWithText>
                    </div>
                </PartnerSection>
                <PartnerSection title="Submit Your Application" headingLevel={2}>
                    <p>
                        After you register your interest below and hear back from us, we&apos;ll need you to provide the
                        following:
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                        <div className={SUBMISSION_CARD_CLASS}>
                            <PartnerIconWithText icon="browser">App details</PartnerIconWithText>
                            <p className="mb-0">
                                General information about the application we&apos;re approving including Client ID,
                                name and description
                            </p>
                        </div>
                        <div className={SUBMISSION_CARD_CLASS}>
                            <PartnerIconWithText icon="badge">Security self-assessment</PartnerIconWithText>
                            <p className="mb-0">
                                Includes scopes requested, credential storage and rate limit-handling
                            </p>
                        </div>
                        <div className={SUBMISSION_CARD_CLASS}>
                            <PartnerIconWithText icon="video">Demo / Access</PartnerIconWithText>
                            <p className="mb-0">A video walkthrough or test access for us to review your application</p>
                        </div>
                        <div className={SUBMISSION_CARD_CLASS}>
                            <PartnerIconWithText icon="store">Marketplace details</PartnerIconWithText>
                            <p className="mb-0">
                                Short and long description plus logo to display within the Shopfront marketplace
                            </p>
                        </div>
                    </div>
                </PartnerSection>
                <div className="my-4">
                    <Banner type={BannerType.Information}>
                        While your application is in review, it can still integrate with stores using Shopfront&apos;s
                        premium plan
                    </Banner>
                </div>
                <div className="text-center">
                    <LinkButton
                        style={ButtonStyle.Filled}
                        colour={ButtonColour.Primary}
                        to="/partner/register"
                    >
                        Register Interest
                    </LinkButton>
                </div>
            </BoxForm>
        </main>
    );
};

export default PartnerProgram;
